import type { Video } from '../../../generated/graphql-manager';
import type { PlayerApi, PlayerConfig } from '@movingimage-evp/player-library';

import {
  CloseIcon,
  IconButton,
  InputDescription,
  ModalPortal,
  PrimaryButton,
  SecondaryButton,
  TextInput,
  Tooltip,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { Player } from '@movingimage-evp/player-library';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from 'src/components/info-tooltip';
import { secondsToMMSS } from 'src/utils';

import { fetchVideoDetails } from '../../../common';
import { getLocale } from '../../../i18n';

import styles from './video-details-modal.module.css';

type Props = {
  isOpen: boolean;
  video?: Video;
  enableOffsetSelection?: boolean;
  offsetInSeconds?: number;
  onConfirm: (video: Video, offsetInSeconds?: number) => void;
  onClose: () => void;
};

export function VideoDetailsModal({
  isOpen,
  video,
  offsetInSeconds,
  enableOffsetSelection,
  onConfirm,
  onClose,
}: Props) {
  const locale = getLocale();
  const { t } = useTranslation();
  const [playerConfig, setPlayerConfig] = useState<PlayerConfig>();
  const [error, setError] = useState<string>();
  const [startTime, setStartTime] = useState<string | undefined>();
  const [startTimeValidationError, setStartTimeValidationError] = useState<string | undefined>();
  const [updatedOffsetInSeconds, setUpdatedOffsetInSeconds] = useState<number | undefined>(offsetInSeconds);

  const handleClose = () => {
    setPlayerConfig(undefined);
    setError(undefined);
    onClose();
  };

  const handleConfirm = () => {
    if (!video) return;

    onConfirm(video, updatedOffsetInSeconds);
    handleClose();
  };

  useEffect(() => {
    if (video && offsetInSeconds) {
      setStartTime(secondsToMMSS(offsetInSeconds));
    }
  }, [video, offsetInSeconds]);

  const playerApiRef = useRef<PlayerApi | null>(null);

  const playerReadyHandler = useCallback(
    (api: PlayerApi) => {
      if (enableOffsetSelection) {
        playerApiRef.current = api;
        playerApiRef.current?.setCurrentTime(offsetInSeconds || 0);
      }
    },
    [enableOffsetSelection, offsetInSeconds]
  );

  const formatTime = (value: string) => {
    const timeParts = value.split(':').map((part) => parseInt(part, 10));

    // Format should be hh:mm
    if (!/^\d{1,3}:\d{2}$/.test(value) || timeParts.length !== 2) {
      return {
        value: 0,
        error: t('components.videoManager.videoBrowser.videoDetailsModal.startTime.validation.wrongFormat'),
      };
    }

    const [minutes, seconds] = timeParts;
    const offsetInSeconds = minutes * 60 + seconds;
    if (video && video.duration < offsetInSeconds * 1000) {
      return {
        value: 0,
        error: t('components.videoManager.videoBrowser.videoDetailsModal.startTime.validation.timeExceedsVideoLength'),
      };
    }

    return { value: offsetInSeconds };
  };

  const onStartTimeBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    const offsetInSeconds = formatTime(value);

    if (offsetInSeconds.error) {
      return setStartTimeValidationError(offsetInSeconds.error);
    }

    setUpdatedOffsetInSeconds(offsetInSeconds.value);
    playerApiRef.current?.setCurrentTime(offsetInSeconds.value);
    setStartTimeValidationError(undefined);
  };

  const onStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();

    setStartTime(value);

    const offsetInSeconds = formatTime(value);
    if (!offsetInSeconds.error && playerApiRef) {
      playerApiRef.current?.setCurrentTime(offsetInSeconds.value);
    }
  };

  useEffect(() => {
    if (!video) return;

    // Clear video data so that the Player is properly re-rendered with a new one
    setPlayerConfig(undefined);

    let ignoreResult = false;

    fetchVideoDetails(video.id, video.channelId)
      .then((videoData) => {
        if (ignoreResult) return;
        setPlayerConfig(videoData.playerData);
      })
      .catch((error) => {
        if (error.status === 401) return setError(t('errors.coverVideo.hasSecurityPolicy'));
        if (error.status === 404) return setError(t('errors.coverVideo.notFound'));
        setError(t('errors.coverVideo.unknown'));
      });

    return () => {
      ignoreResult = true;
    };
  }, [video, t]);

  useEffect(() => {
    if (isOpen) {
      setError(undefined);
      setStartTimeValidationError(undefined);
    }
  }, [isOpen]);

  // Because this modal is nested inside the VideoBrowser modal, we need to prevent the ModalPortal from rendering when it's closed
  // in order to not overwrite the logic of adding the "modal-open" class to the body, as that breaks Select's UI.
  if (!isOpen) return null;

  return (
    <ModalPortal isOpen={isOpen} onClose={handleClose}>
      <div className={classNames(styles.modal, 'lspro-scrollbars')} data-testid="video-details-modal">
        <div className={styles.player}>
          {playerConfig && <Player {...playerConfig} onReady={playerReadyHandler} />}
          {error}
        </div>

        <div className={styles.content}>
          <div className={styles.header}>
            <h3>{t('components.videoManager.videoBrowser.videoDetailsModal.details')}</h3>

            <Tooltip label={t('components.videoManager.videoBrowser.videoDetailsModal.close')}>
              <IconButton
                rounded
                onClick={handleClose}
                aria-label={t('components.videoManager.videoBrowser.videoDetailsModal.close')}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div className={styles.info}>
            <div className={styles.item}>
              <span>{t('components.videoManager.videoBrowser.videoDetailsModal.fileName')}</span>
              <span data-testid="video-details-modal-file-name">{video?.title}</span>
            </div>

            <div className={styles.item}>
              <span>{t('components.videoManager.videoBrowser.videoDetailsModal.date')}</span>

              <span data-testid="video-details-modal-date">
                {format(new Date(video?.createdDate || 0), 'PP', { locale })}
              </span>
            </div>

            {enableOffsetSelection && (
              <div className={styles.item}>
                <div className={styles.startTimeLabel}>
                  {t('components.videoManager.videoBrowser.videoDetailsModal.startTime.label')}
                  <InfoTooltip label={t('components.videoManager.videoBrowser.videoDetailsModal.startTime.info')} />
                </div>
                <InputDescription
                  inputId="video-details-modal-start-time-input"
                  errorMessage={startTimeValidationError}
                >
                  <TextInput
                    data-testid="video-details-modal-start-time-input"
                    className={styles.startTime}
                    placeholder="00:00"
                    value={startTime}
                    onChange={onStartTimeChange}
                    onBlur={onStartTimeBlur}
                  />
                </InputDescription>
              </div>
            )}
          </div>

          <div className={styles.actions}>
            <PrimaryButton
              data-testid="video-details-modal-confirm-button"
              disabled={!video || error !== undefined || startTimeValidationError !== undefined}
              onClick={handleConfirm}
            >
              {t('components.videoManager.videoBrowser.videoDetailsModal.buttons.confirm')}
            </PrimaryButton>

            <SecondaryButton data-testid="video-details-modal-cancel-button" onClick={handleClose}>
              {t('components.videoManager.videoBrowser.videoDetailsModal.buttons.cancel')}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
